import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const APP_ID = process.env.REACT_APP_FB_APP_ID;
const SDK_VERSION = process.env.REACT_APP_FB_SDK_VERSION;

const loadSdk = () => new Promise((resolve, reject) => {
  const init = () => { window.FB.init({ appId: APP_ID, autoLogAppEvents: true, xfbml: true, version: SDK_VERSION }); resolve(window.FB); };
  if (window.FB) return init();
  window.fbAsyncInit = init;
  const s = Object.assign(document.createElement("script"), { src: "https://connect.facebook.net/en_US/sdk.js", async: true, defer: true, crossOrigin: "anonymous" });
  s.onerror = () => reject(new Error("Impossibile caricare il Facebook SDK"));
  document.body.appendChild(s);
});

const STANDARD_WARNING = "Collegato in modalità API standard, non Coexistence: il numero va registrato con PIN (POST /{PHONE_NUMBER_ID}/register)";

const STEP_LABELS = { subscribed_apps: "Iscrizione app al WABA", smb_app_state_sync: "Sincronizzazione rubrica", history: "Sincronizzazione storico" };

export const ConnectWhatsApp = ({ settings }) => {
  const qc = useQueryClient();
  const session = useRef({});
  const [busy, setBusy] = useState(false);
  const [steps, setSteps] = useState(null);

  const finish = async () => {
    const { code, waba_id, phone_number_id, mode } = session.current;
    if (!code || !waba_id || !phone_number_id) return;
    session.current = {};
    try {
      const { data } = await api.post("/onboarding/embedded-signup", { code, waba_id, phone_number_id, mode });
      setSteps(data.steps);
      if (data.mode === "standard") toast.warning(STANDARD_WARNING, { duration: 15000 });
      else data.ok ? toast.success("WhatsApp collegato in Coexistence") : toast.warning("Collegato, ma alcuni passaggi non sono riusciti");
      qc.invalidateQueries({ queryKey: ["settings"] });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const finishRef = useRef(finish);
  finishRef.current = finish;

  useEffect(() => {
    const onMessage = (event) => {
      if (!/^https:\/\/([a-z0-9-]+\.)*facebook\.com$/.test(event.origin)) return;
      let d;
      try { d = JSON.parse(event.data); } catch { return; }
      if (d.type !== "WA_EMBEDDED_SIGNUP") return;
      if (d.event === "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING" || d.event === "FINISH") {
        const mode = d.event === "FINISH" ? "standard" : "coexistence";
        session.current = { ...session.current, waba_id: d.data.waba_id, phone_number_id: d.data.phone_number_id, mode };
        finishRef.current();
      } else if (d.event === "CANCEL") {
        setBusy(false);
        if (d.data?.error_message) toast.error(`${d.data.error_message} (error_code: ${d.data.error_code ?? "n/d"}, session_id: ${d.data.session_id ?? "n/d"})`, { duration: 15000 });
        else toast.info(`Collegamento annullato${d.data?.current_step ? ` (passo: ${d.data.current_step})` : ""}`);
      } else if (d.event === "ERROR") {
        setBusy(false);
        toast.error(d.data?.error_message || "Errore durante il collegamento");
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = async () => {
    const CONFIG_ID = (settings.wa_es_config_id || "").trim();
    if (!CONFIG_ID || !settings.wa_app_secret_set) return toast.error("Salva prima Configuration ID e App Secret");
    setBusy(true);
    try {
      const FB = await loadSdk();
      FB.login((resp) => {
        if (resp.authResponse?.code) { session.current = { ...session.current, code: resp.authResponse.code }; finishRef.current(); }
        else setBusy(false);
      }, {
        config_id: CONFIG_ID,
        response_type: "code",
        override_default_response_type: true,
        extras: { setup: {}, featureType: "whatsapp_business_app_onboarding", sessionInfoVersion: "3" },
      });
    } catch (e) { setBusy(false); toast.error(e.message); }
  };

  return (
    <div className="border border-[#25D366]/40 bg-[#25D366]/5 rounded-lg p-4 space-y-3" data-testid="connect-whatsapp-panel">
      <div className="flex flex-wrap items-center gap-3">
        <Button data-testid="connect-whatsapp-button" disabled={busy} onClick={connect} className="bg-[#25D366] hover:bg-[#22C55E] text-slate-900 font-semibold">
          <Link2 size={15} className="mr-1.5" />{busy ? "Collegamento in corso…" : "Collega WhatsApp"}
        </Button>
        {settings.coex_onboarded_at && <span data-testid="connect-whatsapp-done" className="text-xs font-semibold text-[#15803D]">Collegato il {new Date(settings.coex_onboarded_at).toLocaleString("it-IT")}</span>}
      </div>
      {settings.wa_onboard_mode === "standard" && <p data-testid="connect-whatsapp-standard-warning" className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-md px-3 py-2">{STANDARD_WARNING}</p>}
      <p className="text-[11px] text-slate-500">Embedded Signup v4 in Coexistence: scansiona il QR con l'app WhatsApp Business sul telefono. Il numero non viene ri-registrato; rubrica e storico (180 giorni) partono subito, una sola volta.</p>
      {steps && (
        <ul className="text-xs space-y-1" data-testid="connect-whatsapp-steps">
          {Object.entries(steps).map(([k, v]) => (
            <li key={k} className="flex items-center gap-1.5">
              {v.ok ? <CheckCircle2 size={13} className="text-[#15803D]" /> : <XCircle size={13} className="text-red-600" />}
              {STEP_LABELS[k]}{v.request_id ? ` · request_id ${v.request_id}` : ""}{v.detail ? ` · ${v.detail}` : ""}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
